import React from 'react';
import PropTypes from 'prop-types';

import Select from '../../../../components/Select';
import Strings from '../../../../Strings';
import accessStyles from '../AccessSimulator/AccessSimulator.module.scss';
import { DraftField, toIntOrNull, toTextOrNull } from './fields';

const TEXT_FIELDS = [
  ['first_name', 'firstName'],
  ['last_name', 'lastName'],
  ['provider_type', 'athenaSimProviderType'],
  ['specialty', 'athenaSimSpecialty'],
  ['ansi_specialty_code', 'athenaSimTaxonomy'],
  ['phone', 'athenaSimPhone'],
  ['fax', 'athenaSimFax'],
  ['direct_address', 'athenaSimDirectAddress'],
  ['address1', 'athenaSimAddress'],
  ['city', 'athenaSimCity'],
  ['state', 'athenaSimState'],
  ['zip', 'athenaSimZip'],
];

const NUMBER_FIELDS = [
  ['practice_id', 'athenaSimPractice'],
  ['provider_id', 'athenaSimAthenaId'],
  ['npi', 'athenaSimNpi'],
  ['specialty_id', 'athenaSimSpecialtyId'],
];

const KIND_OPTIONS = [
  { value: 'provider', label: Strings.athenaSimKindProvider },
  { value: 'referring', label: Strings.athenaSimKindReferring },
];

// The draft keeps every field as the text the tester typed.
export const emptyProviderDraft = practiceId => ({
  kind: 'provider',
  ...Object.fromEntries([...TEXT_FIELDS, ...NUMBER_FIELDS].map(([field]) => [field, ''])),
  practice_id: String(practiceId),
});

export const providerDraftFrom = provider => Object.fromEntries(
  Object.entries({ ...emptyProviderDraft(''), ...provider }).map(([field, value]) => [field, value ?? '']),
);

// Mirrors the backend validation so Save cannot produce a 400.
export const providerDraftIsValid = draft => (
  !!draft.first_name.trim() && !!draft.last_name.trim() && Number(draft.practice_id) > 0
  && (draft.npi === '' || /^\d{10}$/.test(String(draft.npi)))
);

export const providerFromDraft = draft => ({
  kind: draft.kind,
  ...Object.fromEntries(TEXT_FIELDS.map(([field]) => [field, toTextOrNull(draft[field])])),
  ...Object.fromEntries(NUMBER_FIELDS.map(([field]) => [field, toIntOrNull(draft[field])])),
  first_name: draft.first_name.trim(),
  last_name: draft.last_name.trim(),
});

/**
 * Add/edit form of a simulated provider: a rendering provider of the practice
 * (served as /providers, e.g. a patient's PCP) or a referring provider (served
 * as /referringproviders).
 */
function ProviderModal({ draft, setDraft, isSaving }) {
  const setField = (field, value) => setDraft({ ...draft, [field]: value });
  return (
    <div className={accessStyles.patientModal}>
      <div className={accessStyles.patientModalIdentity}>
        <div className={accessStyles.patientModalField}>
          {Strings.athenaSimKind}
          <Select
            id="athena-sim-provider-kind"
            data={KIND_OPTIONS}
            value={draft.kind}
            onChange={option => setField('kind', option?.value || 'provider')}
            disabled={isSaving}
          />
        </div>
        {NUMBER_FIELDS.map(([field, label]) => (
          <DraftField key={field} id={`athena-sim-provider-${field}`} label={Strings[label]} type="number" value={draft[field]} onChange={value => setField(field, value)} disabled={isSaving} />
        ))}
        {TEXT_FIELDS.map(([field, label]) => (
          <DraftField key={field} id={`athena-sim-provider-${field}`} label={Strings[label]} value={draft[field]} onChange={value => setField(field, value)} disabled={isSaving} />
        ))}
      </div>
      <p className={accessStyles.patientModalHint}>{Strings.athenaSimProviderHint}</p>
    </div>
  );
}

ProviderModal.propTypes = {
  draft: PropTypes.object.isRequired,
  setDraft: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
};

export default ProviderModal;
